import React, { useState } from 'react'
import { PropTypes } from 'prop-types'
import { Message } from 'semantic-ui-react'
import * as R from 'ramda'
import useAxios, { mutate } from 'hooks/useAxios'
import useModal from 'hooks/useModal'
import useSaveUserActivityLog, { ACCOUNT_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import useUser from 'hooks/useUser'
import { toast } from 'libs/toast'
import ConfirmDeletionModal from 'components/Modals/ConfirmDeletionModal'

import { DnsRecordModalPresentation } from '.'

export function DnsRecordModal({ domain, isOpen, onClose }) {
  const [warningResult, setWarningResult] = useState({})
  const saveUserActivityLog = useSaveUserActivityLog()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)

  const { fetch: validateDnsRecord, loading } = useAxios({
    url: `sendgrid/whitelabel/domains/${domain.id}/validate`,
    method: 'post',
    skip: true,
  })

  const { fetch: deleteDomain, loading: isDeleting } = useAxios({
    url: `sendgrid/whitelabel/domains/${domain.id}`,
    method: 'DELETE',
    skip: true,
  })

  const {
    Modal: DeleteModal,
    open: openDeleteModal,
    close: closeDeleteModal,
    isOpen: isDeleteModalOpen,
  } = useModal()

  const closeModal = () => {
    setWarningResult({})
    onClose()
  }

  const handleDeleteClick = () => {
    openDeleteModal()
  }

  const handleConfirmDelete = () => {
    deleteDomain()
      .then(() => {
        mutate(
          'sendgrid/whitelabel/domains?limit=500',
          (domains) => domains.filter((d) => d.id !== domain.id),
          false
        )
        closeDeleteModal()
        closeModal()
        toast.success('Domain deleted successfully.')
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.detail || 'Could not delete domain. Contact support for help.'
        )
      })
  }

  const handleValidataion = () => {
    validateDnsRecord().then((res) => {
      const data = res.data

      mutate(
        'sendgrid/whitelabel/domains',
        (domains) => {
          if (!domains) {
            return domains
          }
          const domainIndex = R.findIndex(R.propEq('id', data.id), domains)
          const matchedDomain = domains[domainIndex]
          if (!matchedDomain) {
            return domains
          }
          const newDomain = R.mergeDeepRight(matchedDomain, {
            dns: R.mergeDeepRight(
              matchedDomain.dns || {},
              R.map(R.pick(['valid']), data.validation_results || {})
            ),
            valid: data.valid,
            last_validation_attempt_at: Date.now(),
          })

          return R.update(domainIndex, newDomain, domains)
        },
        false
      )

      if (data.valid) {
        saveUserActivityLog({
          action: ACCOUNT_ACTION_NAME.authenticated_email_domain,
          statusCode: res.status,
          requestBody: data,
          target: `com_${companyId}`,
        })
      }

      setWarningResult(data.validation_results)
    })
  }

  return (
    <>
      <DnsRecordModalPresentation
        isModalOpen={isOpen}
        domain={domain}
        closeModal={closeModal}
        loading={loading}
        handleValidataion={handleValidataion}
        warningResult={warningResult}
        onDeleteClick={handleDeleteClick}
      />
      {isDeleteModalOpen && (
        <ConfirmDeletionModal
          modal={DeleteModal}
          close={closeDeleteModal}
          handleDelete={handleConfirmDelete}
          loading={isDeleting}
          title="Delete domain"
          description={
            <>
              {domain.is_used && (
                <Message warning>
                  <strong>{domain.domain}</strong> is used by one or more surveys. If you delete it,
                  those surveys will send from @simplesat-mail.com until you choose a new sending
                  domain.
                </Message>
              )}
              This will delete <strong>{domain.domain}</strong> from your account. This action
              cannot be undone.
            </>
          }
          buttonLabel="Delete domain"
        />
      )}
    </>
  )
}

DnsRecordModal.propTypes = {
  domain: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/DnsRecordModal/container.js